import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductSearch = ({ products, searchQuery, onSearchChange, onAddToCart }) => {
  const filteredProducts = useMemo(() => {
    if (!searchQuery?.trim()) return products;
    
    const query = searchQuery?.toLowerCase();
    return products?.filter(p => 
      p?.name?.toLowerCase()?.includes(query) ||
      p?.sku?.toLowerCase()?.includes(query) ||
      p?.barcode?.toLowerCase()?.includes(query) ||
      p?.product_categories?.name?.toLowerCase()?.includes(query)
    );
  }, [products, searchQuery]);

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Search Header */}
      <div className="p-4 border-b border-border">
        <div className="relative">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e?.target?.value)}
            placeholder="Search products by name, SKU, or barcode..."
            className="w-full pl-10 pr-4 py-3 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          />
        </div>
      </div>

      {/* Product List */}
      <div className="p-4 max-h-[600px] overflow-y-auto">
        {filteredProducts?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery ? 'No products found' : 'No products available'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filteredProducts?.map((product) => (
              <div
                key={product?.id}
                className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border hover:border-primary transition-smooth cursor-pointer"
                onClick={() => onAddToCart?.(product)}
              >
                {/* Product Image */}
                <div className="w-16 h-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
                  {product?.image_url ? (
                    <img
                      src={product?.image_url}
                      alt={product?.name}
                      className="w-full h-full object-cover rounded"
                    />
                  ) : (
                    <Icon name="Package" size={24} className="text-muted-foreground" />
                  )}
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate">{product?.name}</h4>
                  <p className="text-xs text-muted-foreground truncate">
                    {product?.product_categories?.name || 'Uncategorized'} • SKU: {product?.sku}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-lg font-bold text-primary">
                      ${product?.base_price?.toFixed(2)}
                    </p>
                    {product?.track_inventory && (
                      <p className="text-xs text-muted-foreground">
                        Stock: {product?.stock_quantity}
                      </p>
                    )}
                  </div>
                </div>

                {/* Add Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onAddToCart?.(product);
                  }}
                >
                  <Icon name="Plus" size={18} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSearch;